/**
 * RTWO Cryptography & Identity Engine — Identity Verification Service
 */

import { CRYPTO_SERVICE_IDS } from '../domain-models';
import type { IdentityId, IdentityStatus } from '../domain-models';
import type {
  IIdentityVerificationService,
  IdentityVerificationResult,
} from '../service-interfaces';
import type { CryptoRepositories } from '../repositories/crypto-repositories';
import type { IdentityBackendRegistry } from '../registry/provider-registries';
import { CryptoEventTypes } from '../events/crypto-event-bus';
import { BaseCryptoService, type CryptoServiceContext } from './base-crypto-service';

export class IdentityVerificationService
  extends BaseCryptoService
  implements IIdentityVerificationService
{
  readonly serviceId = CRYPTO_SERVICE_IDS.IDENTITY_VERIFICATION;
  readonly serviceName = 'Identity Verification Service';
  readonly description = 'Manages identity verification lifecycle via Identity Backend.';
  readonly layer = 'R7' as const;
  readonly integrations = ['System Registry', 'R7 Enterprise Services'];
  readonly requiresKeyProvider = false;
  readonly requiresIdentityBackend = true;

  constructor(
    context: CryptoServiceContext,
    private readonly repositories: CryptoRepositories,
    private readonly identityBackendRegistry: IdentityBackendRegistry
  ) {
    super(context);
  }

  async submitForVerification(identityId: IdentityId): Promise<IdentityVerificationResult> {
    const identity = this.repositories.identities.findById(identityId);
    if (!identity || !this.identityBackendRegistry.isConfigured()) {
      return {
        verified: false,
        identityId: null,
        error: 'Identity Backend not connected. Verification not available.',
        isPlaceholder: true,
        source: 'Identity Backend — Not Connected',
      };
    }

    await this.identityBackendRegistry.getActiveBackend().verifyIdentity(identityId);
    this.context.eventBus.emit(CryptoEventTypes.IDENTITY_VERIFIED, identityId, {
      action: 'submit_for_verification',
    });

    return {
      verified: false,
      identityId: null,
      error:
        'Verification submitted but interface remains placeholder until backend certification.',
      isPlaceholder: true,
      source: 'Identity Backend — Not Connected',
    };
  }

  getVerificationStatus(
    identityId: IdentityId
  ): 'not_connected' | 'pending' | 'verified' | 'failed' {
    if (!this.identityBackendRegistry.isConfigured()) {
      return 'not_connected';
    }

    const identity = this.repositories.identities.findById(identityId);
    return identity?.verificationStatus ?? 'not_connected';
  }

  async transitionIdentityStatus(
    identityId: IdentityId,
    newStatus: IdentityStatus
  ): Promise<IdentityVerificationResult> {
    const identity = this.repositories.identities.findById(identityId);
    if (!identity || !this.identityBackendRegistry.isConfigured()) {
      return {
        verified: false,
        identityId: null,
        error: 'Identity Backend not connected. Status transition not available.',
        isPlaceholder: true,
        source: 'Identity Backend — Not Connected',
      };
    }

    const updated = await this.identityBackendRegistry
      .getActiveBackend()
      .updateIdentityStatus(identityId, newStatus);

    if (updated) {
      const stored = this.repositories.identities.findById(identityId);
      if (stored) {
        this.repositories.identities.save({
          ...stored,
          status: newStatus,
          updatedAt: Date.now(),
        });
      }

      if (newStatus === 'suspended') {
        this.context.eventBus.emit(CryptoEventTypes.IDENTITY_SUSPENDED, identityId, { newStatus });
      } else if (newStatus === 'revoked') {
        this.context.eventBus.emit(CryptoEventTypes.IDENTITY_REVOKED, identityId, { newStatus });
      }
    }

    return {
      verified: false,
      identityId: null,
      error:
        'Status transition processed but interface remains placeholder until backend certification.',
      isPlaceholder: true,
      source: 'Identity Backend — Not Connected',
    };
  }
}
